import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useAuth } from '@/providers/AuthProvider';

interface PendingSubmission {
  id: string;
  student_name: string | null;
  student_class: string | null;
  submitted_at: string;
  has_pending_review: boolean;
  pendingReviewCount: number;
  totalQuestions: number;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('zh-HK', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PendingReviewsPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [submissions, setSubmissions] = useState<PendingSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!assignmentId || !user) return;
    loadSubmissions();
  }, [assignmentId, user]);

  const loadSubmissions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/assignments/${assignmentId}/pending-reviews`, {
        headers: { 'x-teacher-id': user!.id },
      });
      if (!res.ok) throw new Error((await res.json()).error || '載入失敗');
      const data = await res.json();
      setSubmissions(data.submissions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const pendingCount = submissions.filter((s) => s.has_pending_review).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/assignments/${assignmentId}`)}
          variant="text"
        >
          返回作業
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          開放式題目審核
        </Typography>
        {pendingCount > 0 && (
          <Chip label={`${pendingCount} 份待審核`} color="warning" size="small" />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {submissions.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="h6">沒有待審核的提交</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            所有已批改的提交都無需審核
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>學生</TableCell>
                <TableCell>班別</TableCell>
                <TableCell>提交時間</TableCell>
                <TableCell align="center">待審核題數</TableCell>
                <TableCell align="center">狀態</TableCell>
                <TableCell align="right">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {submissions.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell>
                    <Typography fontWeight={600}>{sub.student_name || '未知學生'}</Typography>
                  </TableCell>
                  <TableCell>{sub.student_class || '—'}</TableCell>
                  <TableCell>{formatDate(sub.submitted_at)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${sub.pendingReviewCount} / ${sub.totalQuestions}`}
                      color={sub.pendingReviewCount > 0 ? 'warning' : 'success'}
                      size="small"
                      variant={sub.pendingReviewCount > 0 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {sub.has_pending_review ? (
                      <Chip label="待審核" color="warning" size="small" />
                    ) : (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="已完成"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="contained"
                      color={sub.has_pending_review ? 'warning' : 'primary'}
                      startIcon={<RateReviewIcon />}
                      onClick={() =>
                        navigate(
                          `/assignments/${assignmentId}/submissions/${sub.id}/review`,
                        )
                      }
                    >
                      {sub.has_pending_review ? '前往審核' : '查看結果'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
